import { getGmailAccessToken } from '../store/useGmailStore';

/* Sending through the Room Parent's own connected Gmail account, straight from
   the browser — no backend hop. users/messages/send files the message in that
   account's Sent folder, so what parents receive and what the sender sees in
   Gmail are the same message. Needs the gmail.send scope the connect flow asks
   for. */
/* Every send, attachments included, goes through this JSON call with the whole
   message base64url'd in `raw`. Google also offers /upload/gmail/v1/…/send
   (media, multipart and resumable) for big messages, but from a browser it is
   unusable: the upload server answers the CORS preflight and then returns the
   real response — the 200 included — without an Access-Control-Allow-Origin
   header, so fetch throws after the mail has already gone out and the page
   has no way to tell a sent message from a failed one (seen 6 Oct). The JSON
   endpoint sends the header, and its gateway took a 40 MB body in a probe;
   Gmail's own ceiling is a 35 MB message, which the 25 MB attachment cap in
   utils/attachments stays under once base64 is counted. */
const GMAIL_SEND_URL = 'https://gmail.googleapis.com/gmail/v1/users/me/messages/send';

/* base64url over UTF-8 — btoa alone throws on anything non-Latin-1. A built
   message is pure ASCII (headers are RFC 2047 words, bodies are base64), so
   it goes straight to btoa; with 30 MB of attachments the byte-by-byte path
   below would take seconds and most of the tab's memory. */
function base64Url(str) {
  let b64;
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7F]*$/.test(str)) {
    b64 = btoa(str);
  } else {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    b64 = btoa(binary);
  }
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

/* Headers are ASCII-only on the wire; anything else goes as an RFC 2047 word. */
function encodeHeader(value) {
  const v = String(value || '');
  // eslint-disable-next-line no-control-regex
  return /^[\x00-\x7F]*$/.test(v) ? v : `=?UTF-8?B?${base64(v)}?=`;
}

const wrap76 = (s) => s.replace(/(.{1,76})/g, '$1\r\n').trim();

/* A readable From when we know the sender's name: "Name <address>". */
const addressWithName = (name, email) => (name ? `${encodeHeader(name)} <${email}>` : email);

/* a file name inside a header: quotes and line breaks out, non-ASCII encoded */
const headerName = (name) => encodeHeader(String(name || 'file').replace(/["\r\n]/g, '_'));

/* A file as the browser read it (data: URL) → the base64 Gmail wants. */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(new Error(`Could not read ${file.name}`));
    r.readAsDataURL(file);
  });
}

/* ── MIME parts ──
   The message is built from the inside out:
     text/html, or multipart/related when inline images point at it (<img src="cid:…">)
     multipart/alternative when there is a plain-text twin — some mail apps show only that
     multipart/mixed when files are attached
   Each part is { headers, body }, bodies already base64 and wrapped at 76. */
const part = (headers, body) => ({ headers, body });

const boundary = (tag) => `rp_${tag}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

function multipart(type, parts) {
  const b = boundary(type);
  return part(
    [`Content-Type: multipart/${type}; boundary="${b}"`],
    `${parts.map((p) => `--${b}\r\n${p.headers.join('\r\n')}\r\n\r\n${p.body}`).join('\r\n')}\r\n--${b}--`
  );
}

const htmlPart = (html) =>
  part(['Content-Type: text/html; charset="UTF-8"', 'Content-Transfer-Encoding: base64'], wrap76(base64(html || '')));

const textPart = (text) =>
  part(['Content-Type: text/plain; charset="UTF-8"', 'Content-Transfer-Encoding: base64'], wrap76(base64(text || '')));

/* Mail clients show an inline image from the message itself, so it needs no
   public URL: Gmail can't load a localhost address, and it won't show SVG or
   data: URIs at all. */
const inlinePart = (img) =>
  part(
    [
      `Content-Type: ${img.contentType}; name="${headerName(img.filename)}"`,
      'Content-Transfer-Encoding: base64',
      `Content-ID: <${img.cid}>`,
      `Content-Disposition: inline; filename="${headerName(img.filename)}"`,
    ],
    wrap76(img.base64)
  );

const filePart = (f) =>
  part(
    [
      `Content-Type: ${f.contentType || 'application/octet-stream'}; name="${headerName(f.filename)}"`,
      'Content-Transfer-Encoding: base64',
      `Content-Disposition: attachment; filename="${headerName(f.filename)}"`,
    ],
    wrap76(f.base64)
  );

function buildBody({ html, text, inlineImages, attachments }) {
  let body = inlineImages.length ? multipart('related', [htmlPart(html), ...inlineImages.map(inlinePart)]) : htmlPart(html);
  if (text) body = multipart('alternative', [textPart(text), body]);
  if (attachments.length) body = multipart('mixed', [body, ...attachments.map(filePart)]);
  return body;
}

/**
 * Send one HTML email as the connected Gmail account.
 * Class emails go out with every family on Bcc so nobody sees anyone else's
 * address; `to` defaults to the sender, which keeps the message well-formed.
 * text: the plain-text twin of the HTML (sent as multipart/alternative).
 * inlineImages: [{ cid, contentType, filename, base64 }] for <img src="cid:…">.
 * attachments: [{ filename, contentType, base64 }] — real attachments.
 */
export async function sendGmailMessage({
  to = [],
  bcc = [],
  subject,
  html,
  text,
  fromName,
  fromEmail,
  inlineImages = [],
  attachments = [],
}) {
  const token = await getGmailAccessToken();
  if (!token) throw new Error('Connect Gmail first, then send.');

  const toList = to.length ? to : fromEmail ? [fromEmail] : [];
  if (!toList.length && !bcc.length) throw new Error('No recipients to send to.');

  const content = buildBody({ html, text, inlineImages, attachments });
  const headers = [
    fromEmail ? `From: ${addressWithName(fromName, fromEmail)}` : null,
    toList.length ? `To: ${toList.join(', ')}` : null,
    bcc.length ? `Bcc: ${bcc.join(', ')}` : null,
    `Subject: ${encodeHeader(subject || '')}`,
    'MIME-Version: 1.0',
    ...content.headers,
  ].filter(Boolean);

  const message = `${headers.join('\r\n')}\r\n\r\n${content.body}`;

  const res = await fetch(GMAIL_SEND_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ raw: base64Url(message) }),
  });

  if (!res.ok) {
    const detail = await res.json().catch(() => null);
    const message = detail?.error?.message || `Gmail refused the send (${res.status})`;
    /* the usual cause: the stored grant predates the gmail.send scope, or it
       was revoked in the Google account */
    throw new Error(
      res.status === 401 || res.status === 403
        ? `${message} — disconnect and reconnect Gmail, then try again.`
        : message
    );
  }
  return res.json();
}
